import { useSyncExternalStore } from 'react'
import type { WhatsNewEntry } from './api/whatsNew'

/**
 * When the app's own announcements keep out of the way (D-731, amends D-651).
 *
 * The welcome and the what's-new dialog are the two modals that appear because
 * the app has something to say rather than because she asked. Both stand down:
 *
 * - **when this page load began at a notification link** (`/n/:id`). She came to
 *   act on something, and the dialog waits for her next ordinary load. The route
 *   resolves and redirects, so this is decided by where the load *began*, never
 *   by where she ended up;
 * - **once she has opened attendance work in this load** - a roster panel or an
 *   attendance sheet. A dialog over a register, or between two registers taken back
 *   to back, is the moment a teacher can least spare. It waits for the next load.
 *
 * Module state, because both are facts about this page load and not about any one
 * component - a dialog that remounts must not forget them.
 */

let loadPath = typeof window === 'undefined' ? '/' : window.location.pathname
let workOpened = false
const listeners = new Set<() => void>()

/*
 * What's new, as this load first saw it, per account (D-731).
 *
 * Module state and not a ref, because the shell remounts inside one load - the
 * header logo leaves the app and `HomeRoute` mounts a fresh one - and a ref would
 * re-read a session refetched after a deploy, or forget a dismissal whose write
 * failed, and show the dialog again with no new load (found by the audit). Keyed by
 * account, because signing out does not reload the page: on a shared family iPad the
 * next person must see her own entries, never post his numbers against her marker.
 */
const shownByAccount = new Map<string, WhatsNewEntry[]>()
const closedAccounts = new Set<string>()

/** The entries this load first received for this account; later refetches are ignored. */
export function whatsNewThisLoad(accountId: string, fromSession: WhatsNewEntry[]): WhatsNewEntry[] {
  if (!shownByAccount.has(accountId)) shownByAccount.set(accountId, fromSession)
  return shownByAccount.get(accountId)!
}

/** She dismissed it: closed for the rest of this load, whatever the write does. */
export function closeWhatsNewThisLoad(accountId: string) {
  closedAccounts.add(accountId)
}

export function whatsNewClosedThisLoad(accountId: string): boolean {
  return closedAccounts.has(accountId)
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Called by a roster panel or an attendance sheet as it opens. */
export function markAttendanceWorkOpened() {
  if (workOpened) return
  workOpened = true
  listeners.forEach((listener) => listener())
}

/** Whether the app's announcements should keep out of the way for the rest of this load. */
export function useDialogsStandDown(): boolean {
  const opened = useSyncExternalStore(subscribe, () => workOpened, () => workOpened)
  return opened || loadPath.startsWith('/n/')
}

/** Tests only: start a fresh page load, beginning at `path`. */
export function newLoadForTests(path = '/') {
  loadPath = path
  workOpened = false
  shownByAccount.clear()
  closedAccounts.clear()
  listeners.forEach((listener) => listener())
}
